<template>
    <div class="bg-white shadow mb-64 rounded p-32 max-w-1440 mx-auto">
        <div class="grid grid-cols-2 gap-16 mb-16">
            <select-dropdown
                static-id="vacancyDropdown"
                v-model="form.job_id"
                :options="jobOptions"
                :auto-translate="false"
                :placeholder="$t('applications.choose_job')">
                {{ $t('applications.job') }} *
            </select-dropdown>

            <date-picker v-model="form.date_start_preferred" disable-past disable-weekends disable-today>
                {{ $t('campaigns.date_start_preferred') }} *
            </date-picker>
        </div>

        <campaign-filters
            v-model="form"
            :job="job"
        />

        <div>
            <wysiwyg
                full-featured
                class="mb-16"
                v-model="form.job_description"
                ref="desc">
                {{ $t('form.description') }}
            </wysiwyg>

            <wysiwyg
                full-featured
                class="mb-16"
                v-model="form.job_requirements"
                ref="req">
                {{ $t('form.requirements') }}
            </wysiwyg>
        </div>
        <div class="pt-32">
            <div class="flex items-center justify-end">
                <p class="italic mr-8" v-if="saving">{{ $t('campaigns.editor.please_wait') }}</p>
                <simple-button
                    display="secondary"
                    icon="save"
                    :loading="saving"
                    look="primary"
                    @click="save('next')"
                >{{ $t('jobs.save_and_next')}}</simple-button>
            </div>
        </div>
    </div>
</template>

<script>
import SelectDropdown from "../../forms/SelectDropdown";
import Form from "../../../etc/forms/Form";
import Job from "../../../models/Job";
import * as jobs from "../../../services/JobService";
import {normalizeJson} from "../../../../shared/helpers";
import Wysiwyg from "../../forms/Wysiwyg";
import SimpleButton from "../SimpleButton";
import Campaign from "../../../models/Campaign";
import DatePicker from "../../forms/DatePickerNew.vue";
import CampaignFilters from "../CampaignFilters";
import moment from 'moment';
import {mapStores} from "pinia";
import {useMainStore} from "../../../state/store";
import {useRepo} from "pinia-orm";

export default {
    components: {CampaignFilters, DatePicker, SimpleButton, Wysiwyg, SelectDropdown},
    props: {
        campaign: Campaign,
        saving: Boolean
    },
    data() {
        return {
            fetchingJobs: false,
            form: this.generateForm(),
        }
    },
    computed: {
        ...mapStores(useMainStore),
        jobs() {
            return useRepo(Job).all().filter(j => !j.is_spontaneous && j.is_published);
        },
        jobOptions() {
            return this.jobs.map(job => ({
                value: job.id,
                label: this.translated(job.title, this.mainStore.locale, true) || this.$t('jobs.missing_title')
            }))
        },
        job() {
            if (!this.form.job_id)
                return null;

            return this.jobs.find(j => parseInt(j.id) === parseInt(this.form.job_id));
            },
    },
    methods: {
        generateForm() {
            const fields = [
                { name: 'step', value: 'channels' },
                { name: 'job_id', value: this.$route.query.job_id ? parseInt(this.$route.query.job_id) : '' },
                { name: 'locale', value: null },
                { name: 'educations', value: [] },
                { name: 'regions', value: [] },
                { name: 'job_profiles', value: [] },
                { name: 'sector', value: null },
                { name: 'job_description', value: '' },
                { name: 'job_requirements', value: '' },
                { name: 'date_start_preferred', value: null },
                { name: 'job_level', value: null },
                { name: 'country_code', value: null }
            ];

            if (this.campaign) {
                fields.forEach((field, index) => {
                    if (this.campaign[field.name] && field.name !== 'step') {
                        fields[index].value = JSON.parse(JSON.stringify(this.campaign[field.name]));

                        if (fields[index].value.length && typeof fields[index].value === 'string' && !isNaN(fields[index].value)) {
                            fields[index].value = +fields[index].value;
                        }
                    }
                });
            }

            return new Form(fields);
        },
        fetchJobs() {
            this.fetchingJobs = true;

            jobs.index({filters: {active: true}, all: true}).then(result => {
                return useRepo(Job).save(normalizeJson(result.data))
            }).then(() => {
                this.fetchingJobs = false;
            });
        },
        setJobTexts() {
            if (!this.job)
                return;

            let description = '';
            let requirements = '';

            if (this.job.description_title[this.form.locale])
                description += `<h2>${this.job.description_title[this.form.locale]}</h2>`;
            if (this.job.description[this.form.locale])
                description += this.job.description[this.form.locale];

            if (this.job.profile_title[this.form.locale])
                requirements += `<h2>${this.job.profile_title[this.form.locale]}</h2>`;
            if (this.job.profile[this.form.locale])
                requirements += this.job.profile[this.form.locale];

            if (this.job.offer_title[this.form.locale])
                requirements += `<h2>${this.job.offer_title[this.form.locale]}</h2>`;
            if (this.job.offer[this.form.locale])
                requirements += this.job.offer[this.form.locale];

            this.form.job_description = description;
            this.$refs.desc.setContent(description);

            this.form.job_requirements = requirements;
            this.$refs.req.setContent(requirements);
        },
        save(todo) {
            this.form.date_start_preferred = this.form.date_start_preferred ? moment(this.form.date_start_preferred).format('YYYY-MM-DD') : null;

            this.$emit('save', {
                form: this.form,
                relationships: {
                    job: { data: { id: this.form.job_id } }
                },
                todo
            });
        }
    },
    mounted() {
        this.fetchJobs();
    },
    watch: {
        "form.job_id"() {
            this.setJobTexts();
        },
        "form.locale"() {
            this.setJobTexts();
        },
        fetchingJobs() {
            this.setJobTexts();
        }
    }
}
</script>
